import { useState } from "react";
import { ExternalLink, Bookmark, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import SignInToSaveModal from "./SignInToSaveModal";

export default function WebDealCard({ deal }) {
  const { user } = useAuth();
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);

  const savePayload = () => ({
    title: deal.title,
    merchant: deal.merchant,
    url: deal.url,
    description: deal.description || "",
    image_url: imgFailed ? "" : (deal.image_url || ""),
    discount: deal.discount || "",
    source_domain: deal.source_domain || "",
    category: deal.category || "misc",
  });

  const save = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      setSignInOpen(true);
      return;
    }
    if (saved || saving) return;
    setSaving(true);
    try {
      await api.post("/web-deals/save", savePayload());
      setSaved(true);
      toast.success("Saved to your bookmarks ✓");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="group relative block border border-[#E5E5E5] bg-white overflow-hidden hover:border-black transition-colors"
      data-testid={`web-deal-${deal.id}`}
    >
      <a
        href={deal.url}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute inset-0 z-[1]"
        aria-label={`Shop ${deal.title}`}
        data-testid={`web-deal-link-${deal.id}`}
      />
      <div className="relative aspect-[4/3] bg-black/5 overflow-hidden">
        {deal.image_url && !imgFailed ? (
          <img
            src={deal.image_url}
            alt={deal.title}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            loading="lazy"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-black/15 font-serif text-6xl">◇</div>
        )}
        {deal.discount && (
          <div className="absolute top-3 left-3 bg-[#E63946] text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1" data-testid={`web-discount-${deal.id}`}>
            {deal.discount}
          </div>
        )}
        <div className="absolute bottom-3 left-3 bg-black/80 text-white text-[9px] font-mono uppercase tracking-widest px-2 py-1 backdrop-blur">
          {deal.source_domain || "web"}
        </div>
        <button
          onClick={save}
          disabled={saving || saved}
          className={`absolute top-3 right-3 z-10 p-2 border ${saved ? "bg-black text-white border-black" : "bg-white/95 text-black border-black/20 hover:bg-black hover:text-white"} backdrop-blur transition-colors disabled:opacity-70`}
          data-testid={`web-save-${deal.id}`}
          title={saved ? "Saved to your bookmarks" : "Save to DealHub"}
        >
          {saved ? <BookmarkCheck className="w-4 h-4" strokeWidth={2} /> : <Bookmark className="w-4 h-4" strokeWidth={2} />}
        </button>
      </div>
      <div className="relative p-5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-black/50 mb-2">
          {deal.merchant}
        </div>
        <div className="font-serif font-bold text-lg leading-tight tracking-tight mb-2 line-clamp-2" data-testid={`web-title-${deal.id}`}>
          {deal.title}
        </div>
        {deal.description && (
          <p className="text-sm text-black/60 leading-relaxed line-clamp-3 mb-3">
            {deal.description}
          </p>
        )}
        <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-[#E63946] group-hover:text-black">
          Shop the deal <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
        </div>
      </div>
      <SignInToSaveModal
        open={signInOpen}
        onClose={() => setSignInOpen(false)}
        payload={savePayload()}
      />
    </div>
  );
}
